package rendering

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.spring
import androidx.compose.animation.core.tween
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import engine.model.Piece
import kotlinx.coroutines.delay

// ── ScoreFeedEntry ────────────────────────────────────────────────────────────

data class ScoreFeedEntry(
    val id: Long,
    val label: String,
    val value: String,
    val isB2B: Boolean = false,
    val isSpin: Boolean = false,
)

// ── Color palette ─────────────────────────────────────────────────────────────

private val COLOR_LABEL      = Color(0xFF888888)
private val COLOR_VALUE      = Color(0xFFFFFFFF)
private val COLOR_ACCENT     = Color(0xFF00E5FF)
private val COLOR_BOX_BORDER = Color(0xFF333355)
private val COLOR_B2B        = Color(0xFFFFD600)
private val COLOR_TIMER      = Color(0xFF00E676)

// ── Text styles ───────────────────────────────────────────────────────────────

@Composable
private fun monoStyle() = TextStyle(
    fontFamily = fontMono(),
    letterSpacing = 0.05.sp,
)

// ── StatLabel ─────────────────────────────────────────────────────────────────

@Composable
fun StatLabel(text: String, modifier: Modifier = Modifier) {
    Text(
        text = text,
        style = monoStyle().copy(fontSize = 9.sp, color = COLOR_LABEL, letterSpacing = 0.12.sp),
        modifier = modifier,
    )
}

// ── StatValue ─────────────────────────────────────────────────────────────────

@Composable
fun StatValue(text: String, modifier: Modifier = Modifier) {
    Text(
        text = text,
        style = monoStyle().copy(fontSize = 18.sp, color = COLOR_VALUE),
        modifier = modifier,
    )
}

// ── HoldBox ───────────────────────────────────────────────────────────────────

@Composable
fun HoldBox(piece: Piece?, modifier: Modifier = Modifier) {
    Column(modifier = modifier, horizontalAlignment = Alignment.CenterHorizontally) {
        StatLabel(text = "HOLD")
        Spacer(modifier = Modifier.height(4.dp))
        Box(
            modifier = Modifier.size(64.dp).border(width = 1.dp, color = COLOR_BOX_BORDER),
            contentAlignment = Alignment.Center,
        ) {
            if (piece != null) PiecePreviewCanvas(piece = piece, modifier = Modifier.size(56.dp))
        }
    }
}

// ── NextBox ───────────────────────────────────────────────────────────────────

@Composable
fun NextBox(piece: Piece?, index: Int, modifier: Modifier = Modifier) {
    val boxSize = if (index == 0) 56.dp else 44.dp
    Column(modifier = modifier, horizontalAlignment = Alignment.CenterHorizontally) {
        if (index == 0) {
            StatLabel(text = "NEXT")
            Spacer(modifier = Modifier.height(4.dp))
        }
        Box(
            modifier = Modifier.size(boxSize).border(width = 1.dp, color = COLOR_BOX_BORDER),
            contentAlignment = Alignment.Center,
        ) {
            if (piece != null) PiecePreviewCanvas(piece = piece, modifier = Modifier.size(boxSize - 8.dp))
        }
    }
}

// ── TopBar ────────────────────────────────────────────────────────────────────

@Composable
fun TopBar(modeName: String, b2bCount: Int, elapsedMs: Long, modifier: Modifier = Modifier) {
    Row(
        modifier = modifier.fillMaxWidth().padding(horizontal = 8.dp, vertical = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.SpaceBetween,
    ) {
        Text(
            text = modeName,
            style = monoStyle().copy(fontSize = 11.sp, color = COLOR_ACCENT, letterSpacing = 0.1.sp),
        )

        if (b2bCount > 0) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(text = "B2B", style = monoStyle().copy(fontSize = 9.sp, color = COLOR_LABEL))
                Spacer(modifier = Modifier.width(4.dp))
                Text(text = "×$b2bCount", style = monoStyle().copy(fontSize = 13.sp, color = COLOR_B2B))
            }
        } else {
            Spacer(modifier = Modifier.width(1.dp))
        }

        Text(
            text = formatElapsedTime(elapsedMs),
            style = monoStyle().copy(fontSize = 11.sp, color = COLOR_TIMER, textAlign = TextAlign.End),
        )
    }
}

// ── ScoreFeedDisplay ──────────────────────────────────────────────────────────

/**
 * Score feed column.
 *
 * - **Clear entries** (non-B2B): zoom-burst in, hold ~1 s, then fade out with
 *   letter-spacing expansion. [onRemove] is called when the animation ends.
 * - **B2B entries**: persistent [SunburstBadge], no auto-remove.
 */
@Composable
fun ScoreFeedDisplay(
    entries: List<ScoreFeedEntry>,
    onRemove: (Long) -> Unit = {},
    modifier: Modifier = Modifier,
) {
    Column(modifier = modifier, verticalArrangement = Arrangement.spacedBy(2.dp)) {
        for (entry in entries) {
            key(entry.id) {
                if (entry.isB2B) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.End,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        SunburstBadge(b2bCount = entry.value.toIntOrNull() ?: 1, size = 36.dp)
                    }
                } else {
                    ClearFeedEntry(entry = entry, onFinished = { onRemove(entry.id) })
                }
            }
        }
    }
}

// ── ClearFeedEntry ────────────────────────────────────────────────────────────

/**
 * Transient clear event entry.
 *
 * 1. Zoom in (150 ms): scale 1.4→1.0, letter-spacing wide→tight, alpha 0→1
 * 2. Hold (~1 050 ms)
 * 3. Fade out (500 ms): scale 1.0→1.15, letter-spacing expands, alpha 1→0
 * 4. Calls [onFinished] for removal.
 */
@Composable
private fun ClearFeedEntry(entry: ScoreFeedEntry, onFinished: () -> Unit) {
    val alpha    = remember { Animatable(0f) }
    val scale    = remember { Animatable(1.4f) }
    val letterSp = remember { Animatable(0.6f) }

    LaunchedEffect(entry.id) {
        // Zoom in
        alpha.animateTo(1f,       animationSpec = tween(150, easing = FastOutSlowInEasing))
        scale.animateTo(1f,       animationSpec = tween(150, easing = FastOutSlowInEasing))
        letterSp.animateTo(0.05f, animationSpec = tween(150, easing = FastOutSlowInEasing))
        // Hold
        delay(1_050)
        // Fade out with letter-spacing burst
        alpha.animateTo(0f,       animationSpec = tween(500, easing = LinearEasing))
        scale.animateTo(1.15f,    animationSpec = tween(500, easing = LinearEasing))
        letterSp.animateTo(0.5f,  animationSpec = tween(500, easing = LinearEasing))
        onFinished()
    }

    val labelColor = when {
        entry.isSpin            -> Color(0xFFAA00FF)
        entry.label.length >= 6 -> Color(0xFF00E5FF)
        else                    -> Color(0xFFCCCCCC)
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .graphicsLayer {
                this.alpha  = alpha.value
                this.scaleX = scale.value
                this.scaleY = scale.value
            },
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = entry.label,
            style = monoStyle().copy(
                fontSize = 11.sp,
                color = labelColor,
                letterSpacing = letterSp.value.sp,
            ),
        )
        if (entry.value.isNotEmpty()) {
            Text(
                text = entry.value,
                style = monoStyle().copy(fontSize = 10.sp, color = Color(0xFFFFD600)),
            )
        }
    }
}

// ── ComboDisplay ──────────────────────────────────────────────────────────────

@Composable
fun ComboDisplay(comboCount: Int, modifier: Modifier = Modifier) {
    val scale = remember { Animatable(1f) }

    LaunchedEffect(comboCount) {
        if (comboCount > 0) {
            scale.snapTo(1.3f)
            scale.animateTo(1f, animationSpec = spring(dampingRatio = 0.4f, stiffness = 400f))
        }
    }

    Column(modifier = modifier, horizontalAlignment = Alignment.CenterHorizontally) {
        if (comboCount > 0) {
            Text(
                text = "×$comboCount",
                style = monoStyle().copy(fontSize = 28.sp, color = Color(0xFFFFD600), letterSpacing = 0.05.sp),
                modifier = Modifier.graphicsLayer { scaleX = scale.value; scaleY = scale.value },
            )
            Text(
                text = "COMBO",
                style = monoStyle().copy(fontSize = 8.sp, color = Color(0xFF888888), letterSpacing = 0.2.sp),
            )
        } else {
            Spacer(modifier = Modifier.height(44.dp))
        }
    }
}

// ── Time formatting ───────────────────────────────────────────────────────────

private fun formatElapsedTime(elapsedMs: Long): String {
    val totalSeconds = (elapsedMs / 1000L).coerceAtLeast(0L)
    val minutes = totalSeconds / 60L
    val seconds = totalSeconds % 60L
    return "${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}"
}
